import { Card, CardHeader, Dropdown, Field, Option, Title3, Subtitle1, Subtitle2, CardPreview, Button } from "@fluentui/react-components";
import { Checkmark20Regular, CheckboxUnchecked20Regular, ReOrderDotsVerticalRegular, CheckboxCheckedRegular, CheckboxUncheckedRegular, EditRegular, LinkRegular, DeleteRegular } from "@fluentui/react-icons";
import { Dispatch, SetStateAction, FC, useState, useEffect } from "react";
import { FactSection, Question, Tendering } from "../../../models/tendering";
import { all } from "axios";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ConfirmationDialog from "../../dialogs/ConfirmationDialog";
import QuestionDialog from "../../dialogs/QuestionDialog";
import SectionEditDialog from "../../dialogs/SectionEditDialog";
import BasicInputModal from "../../dialogs/BasicInputDialog";
import BasicInputDialog from "../../dialogs/BasicInputDialog";

interface TenderingMappingsProps {
  tenderingDocument: Tendering;
  updateTenderingData: Dispatch<SetStateAction<Tendering>>;
}

interface SortableSectionProps {
  section: FactSection;
  onToggleHidden: (section: FactSection) => void;
  onAutoOpen: (section: FactSection) => void;
  onEdit: (section: FactSection) => void;
  onLinkQuestions: (section: FactSection) => void;
  onDelete: (section: FactSection) => void;
  onToggleShowInFacts: (section: FactSection) => void;
  onToggleShowInHiddenFacts: (section: FactSection) => void;
}

const SortableSection: React.FC<SortableSectionProps> = ({
  section,
  onToggleHidden,
  onAutoOpen,
  onEdit,
  onLinkQuestions,
  onDelete,
  onToggleShowInFacts,
  onToggleShowInHiddenFacts,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: section.name,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white border border-gray-300 rounded mb-2"
    >
      <div {...attributes} {...listeners} className="cursor-move">
        <ReOrderDotsVerticalRegular className="text-gray-600" />
      </div>
      <div className="flex-1">{section.name}</div>
      <div className="flex items-center gap-3">
        <div className="flex gap-2 pr-3 border-r border-gray-300">
          <div className="w-12 flex justify-center">
            <Button
              appearance="subtle"
              icon={
                section.showInFacts !== false ? (
                  <CheckboxCheckedRegular />
                ) : (
                  <CheckboxUncheckedRegular />
                )
              }
              onClick={() => onToggleShowInFacts(section)}
              title="Visa i Fakta"
            />
          </div>
          <div className="w-12 flex justify-center">
            <Button
              appearance="subtle"
              icon={
                section.showInHiddenFacts === true ? (
                  <CheckboxCheckedRegular />
                ) : (
                  <CheckboxUncheckedRegular />
                )
              }
              onClick={() => onToggleShowInHiddenFacts(section)}
              title="Visa i Dold fakta"
            />
          </div>
        </div>
        <div className="flex gap-2 pr-3 border-r border-gray-300">
          <div className="w-12 flex justify-center">
            <Button
              appearance="subtle"
              icon={
                section.autoOpenMapping ? (
                  <CheckboxCheckedRegular />
                ) : (
                  <CheckboxUncheckedRegular />
                )
              }
              onClick={() => onAutoOpen(section)}
              title="Öppna sektion automatiskt"
            />
          </div>
          <div className="w-12 flex justify-center">
            <Button
              appearance="subtle"
              icon={
                section.areNegativeAnswersHidden ? (
                  <CheckboxCheckedRegular />
                ) : (
                  <CheckboxUncheckedRegular />
                )
              }
              onClick={() => onToggleHidden(section)}
              title="Dölj fakta om svar Nej"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            appearance="subtle"
            icon={<EditRegular />}
            onClick={() => onEdit(section)}
            title="Redigera sektion"
          />
          <Button
            appearance="subtle"
            icon={<LinkRegular />}
            onClick={() => onLinkQuestions(section)}
            title="Koppla frågor"
          />
          <Button
            appearance="subtle"
            icon={<DeleteRegular />}
            onClick={() => onDelete(section)}
            title="Ta bort sektion"
            className="text-red-600 hover:text-red-700"
          />
        </div>
      </div>
    </div>
  );
};

const SectionColumnHeaders: FC = () => (
  <div className="flex items-center gap-3 px-3 pb-1">
    <div className="w-4" />
    <div className="flex-1" />
    <div className="flex items-center gap-3">
      <div className="flex gap-2 pr-3 border-r border-gray-300">
        <span className="w-12 text-center text-[11px] leading-tight text-gray-500">Fakta</span>
        <span className="w-12 text-center text-[11px] leading-tight text-gray-500">Dold fakta</span>
      </div>
      <div className="flex gap-2 pr-3 border-r border-gray-300">
        <span className="w-12 text-center text-[11px] leading-tight text-gray-500">Auto-öppna</span>
        <span className="w-12 text-center text-[11px] leading-tight text-gray-500">Dölj vid Nej</span>
      </div>
      <div className="flex gap-2">
        <span className="w-[112px] text-center text-[11px] leading-tight text-gray-500">Åtgärder</span>
      </div>
    </div>
  </div>
);

const TenderingMappings: FC<TenderingMappingsProps> = ({
  tenderingDocument,
  updateTenderingData,
}) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [sections, setSections] = useState<FactSection[]>(
    tenderingDocument?.properties?.factSettings?.sections || []
  );
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState<FactSection | null>(null);
  const [sectionEditModalOpen, setSectionEditModalOpen] = useState(false);
  const [createSectionModalOpen, setCreateSectionModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<FactSection | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const allQuestions = tenderingDocument.tenderingParts.flatMap(tp => tp.questions ?? []);
    setQuestions(allQuestions);
  }, [tenderingDocument])

  useEffect(() => {
    setSections(tenderingDocument?.properties?.factSettings?.sections || []);
  }, [tenderingDocument])

  const getQuestionText = (questionId: number | undefined) => {
    if (!questionId || !questions) return '';
    const question = questions.find(q => q.questionID === questionId);
    return question ? `${question.questionID} - ${question.text}` : '';
  };

  const getMultiQuestionText = (questionIds: number[] | undefined) => {
    if (!questionIds || !Array.isArray(questionIds) || questionIds.length === 0 || !questions) return '';
    const selectedQuestions = questionIds.map(id => {
      const question = questions.find(q => q.questionID === id);
      return question ? `${question.questionID} - ${question.text}` : '';
    }).filter(text => text !== '');
    return selectedQuestions.join(', ');
  };

  const handleDropdownChange = (name: string) => (
    event: React.SyntheticEvent,
    data: { optionText: string | undefined; optionValue: string | undefined }
  ) => {
    const value = data.optionValue;

    const [prop1, prop2] = name.split(".");

    if(prop2) {
      updateTenderingData((prev) => ({
        ...prev,
        properties: {
          ...prev.properties,
          [prop1]:{
            ...prev.properties[prop1],
            [prop2]: value !== undefined ? Number(value) : undefined
          }
        }
      }));
    }
    else{
      updateTenderingData((prev) => ({
        ...prev,
        properties: {
          ...prev.properties,
          [prop1]: value !== undefined ? Number(value) : undefined
        }
      }));
    }
  };

  const handleMultiDropdownChange = (name: string) => (
    event: React.SyntheticEvent,
    data: { selectedOptions: string[] }
  ) => {
    const values = data.selectedOptions.map(v => Number(v));

    const [prop1, prop2] = name.split(".");

    updateTenderingData((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        [prop1]:{
          ...prev.properties[prop1],
          [prop2]: values
        }
      }
    }));
  };

    const updateTendering = (updatedSections: FactSection[]) => {
    const updated = {
      ...tenderingDocument,
      properties: {
        ...tenderingDocument.properties,
        factSettings: {
          ...tenderingDocument.properties.factSettings,
          sections: updatedSections,
        },
      },
    };
    if (updateTenderingData) {
      updateTenderingData(updated);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.name === active.id);
      const newIndex = sections.findIndex((s) => s.name === over.id);

      const reordered = arrayMove(sections, oldIndex, newIndex);
      setSections(reordered);
      updateTendering(reordered);
    }
  };

  const handleToggleHidden = (section: FactSection) => {
    const updatedSections = sections.map((s) =>
      s.name === section.name ? { ...s, areNegativeAnswersHidden: !s.areNegativeAnswersHidden } : s
    );
    setSections(updatedSections);
    updateTendering(updatedSections);
  };

  const handleAutoOpen = (section: FactSection) => {
    const updatedSections = sections.map((s) =>
      s.name === section.name ? { ...s, autoOpenMapping: !s.autoOpenMapping } : s
    );
    setSections(updatedSections);
    updateTendering(updatedSections);
  }

  const handleToggleShowInFacts = (section: FactSection) => {
    const updatedSections = sections.map((s) =>
      s.name === section.name ? { ...s, showInFacts: !(s.showInFacts !== false) } : s
    );
    setSections(updatedSections);
    updateTendering(updatedSections);
  }

  const handleToggleShowInHiddenFacts = (section: FactSection) => {
    const updatedSections = sections.map((s) =>
      s.name === section.name ? { ...s, showInHiddenFacts: !(s.showInHiddenFacts === true) } : s
    );
    setSections(updatedSections);
    updateTendering(updatedSections);
  }

  const handleEdit = (section: FactSection) => {
    setSelectedSection(section);
    setSectionEditModalOpen(true);
  };

  const handleLinkQuestions = (section: FactSection) => {
    setSelectedSection(section);
    setQuestionModalOpen(true);
  };

  const handleDelete = (section: FactSection) => {
    setSectionToDelete(section);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (sectionToDelete) {
      const updatedSections = sections.filter((s) => s.name !== sectionToDelete.name);
      setSections(updatedSections);
      updateTendering(updatedSections);
      setSectionToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleQuestionSelect = (data: any) => {
    if (data && data.selected && selectedSection) {
      const factMappings = data.selected.map((element: any) => {
        const exists = selectedSection.mappings?.find((s) => s.questionID === element.questionID);
        return {
          heading: exists ? exists.heading : element.heading,
          text: exists ? exists.text : element.text,
          permission: exists ? exists.permission : 'approval',
          questionID: element.questionID,
          section: selectedSection.name,
          tenderingPartID: element.tenderingPartID,
        };
      });

      const updatedSections = sections.map((s) =>
        s.name === selectedSection.name ? { ...s, mappings: factMappings } : s
      );
      setSections(updatedSections);
      updateTendering(updatedSections);
    }
    setQuestionModalOpen(false);
    setSelectedSection(null);
  };

  const handleSectionEditSave = (updatedSection: FactSection) => {
    const updatedSections = sections.map((s) =>
      s.name === selectedSection?.name ? updatedSection : s
    );
    setSections(updatedSections);
    updateTendering(updatedSections);
    setSectionEditModalOpen(false);
    setSelectedSection(null);
  };

  const handleCreateSection = (name: string) => {
    const newSection: FactSection = { name, mappings: [] };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
    updateTendering(updatedSections);
    setCreateSectionModalOpen(false);
  };

  return(
    <Card>
      <CardPreview>
        <Title3>Kopplingar</Title3>
      </CardPreview>
        <div className="overflow-y-auto p-2">
            <div className="space-y-6 w-full">
        <div>
        <h4 className="text-lg font-semibold mb-4">Sektioner & Koppling av fakta</h4>
        <SectionColumnHeaders />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map((s) => s.name)} strategy={verticalListSortingStrategy}>
            {sections.map((section) => (
              <SortableSection
                key={section.name}
                section={section}
                onToggleHidden={handleToggleHidden}
                onEdit={handleEdit}
                onLinkQuestions={handleLinkQuestions}
                onDelete={handleDelete}
                onAutoOpen={handleAutoOpen}
                onToggleShowInFacts={handleToggleShowInFacts}
                onToggleShowInHiddenFacts={handleToggleShowInHiddenFacts}
              />
            ))}
          </SortableContext>
        </DndContext>

        <div className="flex justify-center mt-4">
          <Button appearance="secondary" onClick={() => setCreateSectionModalOpen(true)}>
            + SKAPA NY SEKTION
          </Button>
        </div>
        </div>

        {/* Question Modal */}
        {questionModalOpen && selectedSection && (
          <QuestionDialog
            isOpen={questionModalOpen}
            onClose={() => {
              setQuestionModalOpen(false);
              setSelectedSection(null);
            }}
            onSelect={handleQuestionSelect}
            tenderingParts={tenderingDocument.tenderingParts}
            singleSelect={false}
            selectedQuestions={selectedSection.mappings?.map((m) => ({ questionID: m.questionID }))}
          />
        )}

        {/* Section Edit Modal */}
        {sectionEditModalOpen && selectedSection && (
          <SectionEditDialog
            isOpen={sectionEditModalOpen}
            onClose={() => {
              setSectionEditModalOpen(false);
              setSelectedSection(null);
            }}
            onSave={handleSectionEditSave}
            section={selectedSection}
          />
        )}

        {/* Create Section Dialog */}
        <BasicInputDialog
          isOpen={createSectionModalOpen}
          onClose={() => setCreateSectionModalOpen(false)}
          onSave={handleCreateSection}
          title="Skapa sektion"
          label="Namn på sektion"
        />

        {/* Delete Confirmation */}
        <ConfirmationDialog
          isOpen={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setSectionToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Radera sektion"
          text={`Radera sektion ${sectionToDelete?.name}?`}
        />
      </div>

      <Subtitle2>
        Översikts koppling
      </Subtitle2>
      <Field label="Översikt text">
        <Dropdown
          name="overviewQuestion"
          value={getQuestionText(tenderingDocument.properties.overviewQuestion)}
          selectedOptions={tenderingDocument.properties.overviewQuestion ? [tenderingDocument.properties.overviewQuestion.toString()] : []}
          onOptionSelect={handleDropdownChange("overviewQuestion")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>

      <Subtitle2>
        Översikts fakta koppling
      </Subtitle2>
      <Field label="Översikt frågor">
        <Dropdown
          name="questionMappings.overviewQuestions"
          value={getMultiQuestionText(tenderingDocument.properties.questionMappings.overviewQuestions)}
          selectedOptions={Array.isArray(tenderingDocument.properties.questionMappings.overviewQuestions) ? tenderingDocument.properties.questionMappings.overviewQuestions.map((id: any) => id.toString()) : []}
          onOptionSelect={handleMultiDropdownChange("questionMappings.overviewQuestions")}
          positioning={{autoSize: false}}
          multiselect
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Subtitle2>
        Kontakt
      </Subtitle2>
      <Field label="E-post">
        <Dropdown
          name="contact.email"
          value={getQuestionText(tenderingDocument.properties.contact.email)}
          selectedOptions={tenderingDocument.properties.contact.email ? [tenderingDocument.properties.contact.email.toString()] : []}
          onOptionSelect={handleDropdownChange("contact.email")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Telefonnummer">
        <Dropdown
          name="contact.phone"
          value={getQuestionText(tenderingDocument.properties.contact.phone)}
          selectedOptions={tenderingDocument.properties.contact.phone ? [tenderingDocument.properties.contact.phone.toString()] : []}
          onOptionSelect={handleDropdownChange("contact.phone")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Subtitle2>
        Kontakt föreståndare
      </Subtitle2>
      <Field label="E-post">
        <Dropdown
          name="questionMappings.contactEmail"
          value={getQuestionText(tenderingDocument.properties.questionMappings.contactEmail)}
          selectedOptions={tenderingDocument.properties.questionMappings.contactEmail ? [tenderingDocument.properties.questionMappings.contactEmail.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.contactEmail")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Telefonnummer">
        <Dropdown
          name="questionMappings.contactPhone"
          value={getQuestionText(tenderingDocument.properties.questionMappings.contactPhone)}
          selectedOptions={tenderingDocument.properties.questionMappings.contactPhone ? [tenderingDocument.properties.questionMappings.contactPhone.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.contactPhone")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Kontaktperson">
        <Dropdown
          name="questionMappings.contactName"
          value={getQuestionText(tenderingDocument.properties.questionMappings.contactName)}
          selectedOptions={tenderingDocument.properties.questionMappings.contactName ? [tenderingDocument.properties.questionMappings.contactName.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.contactName")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Subtitle2>
        Plats & Info Koppling
      </Subtitle2>
      <Field label="Verksamhetens namn">
        <Dropdown
          name="questionMappings.tenderName"
          value={getQuestionText(tenderingDocument.properties.questionMappings.tenderName)}
          selectedOptions={tenderingDocument.properties.questionMappings.tenderName ? [tenderingDocument.properties.questionMappings.tenderName.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.tenderName")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Koncern">
        <Dropdown
          name="questionMappings.organization"
          value={getQuestionText(tenderingDocument.properties.questionMappings.organization)}
          selectedOptions={tenderingDocument.properties.questionMappings.organization ? [tenderingDocument.properties.questionMappings.organization.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.organization")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Org. nr.">
        <Dropdown
          name="questionMappings.organizationNr"
          value={getQuestionText(tenderingDocument.properties.questionMappings.organizationNr)}
          selectedOptions={tenderingDocument.properties.questionMappings.organizationNr ? [tenderingDocument.properties.questionMappings.organizationNr.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.organizationNr")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Besök adress">
        <Dropdown
          name="questionMappings.adress"
          value={getMultiQuestionText(tenderingDocument.properties.questionMappings.adress)}
          selectedOptions={Array.isArray(tenderingDocument.properties.questionMappings.adress) ? tenderingDocument.properties.questionMappings.adress.map((id: any) => id.toString()) : []}
          onOptionSelect={handleMultiDropdownChange("questionMappings.adress")}
          positioning={{autoSize: false}}
          multiselect
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Koordinater">
        <Dropdown
          name="questionMappings.coordinates"
          value={getQuestionText(tenderingDocument.properties.questionMappings.coordinates)}
          selectedOptions={tenderingDocument.properties.questionMappings.coordinates ? [tenderingDocument.properties.questionMappings.coordinates.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.coordinates")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Subtitle2>
        Kontakt Ekonomi
      </Subtitle2>
      <Field label="E-post notiser avtalsadministrativ avgift">
        <Dropdown
          name="questionMappings.economyContactEmail"
          value={getQuestionText(tenderingDocument.properties.questionMappings.economyContactEmail)}
          selectedOptions={tenderingDocument.properties.questionMappings.economyContactEmail ? [tenderingDocument.properties.questionMappings.economyContactEmail.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.economyContactEmail")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      <Field label="Kundnummer ekonomisystem">
        <Dropdown
          name="questionMappings.billingNr"
          value={getQuestionText(tenderingDocument.properties.questionMappings.billingNr)}
          selectedOptions={tenderingDocument.properties.questionMappings.billingNr ? [tenderingDocument.properties.questionMappings.billingNr.toString()] : []}
          onOptionSelect={handleDropdownChange("questionMappings.billingNr")}
          clearable
          positioning={{autoSize: false}}
          listbox={{
            style: {
              maxHeight: '300px',
              overflowY: 'auto'
            },
          }}
        >
          {questions?.map(q => { 
            return (
              <Option 
                key={q.questionID} 
                value={q.questionID.toString()}
                text={`${q.questionID} - ${q.text}`}
              >
                {q.questionID} - {q.text}
              </Option>
            );
          })}
        </Dropdown>
      </Field>
      </div>
    </Card>
  )
}

export default TenderingMappings;